import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  FormControl,
  FormHelperText,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  SxProps,
  Theme,
} from '@mui/material';
import React from 'react';
import { useDisableWheelOnNumberInput } from './useDisableWheelOnNumberInput';

export type ErrorStatus =
  | { tag: 'Submited'; errors: string[] }
  | { tag: 'Not submitted' };

type HelperVariant = 'warning' | 'info';

type InputType = 'tel' | 'email' | 'password' | 'number';

type InputMode =
  | 'text' // (default value)
  | 'decimal'
  | 'numeric'
  | 'tel'
  | 'email';

type InputMultiTypeProps = {
  defaultValue?: string | number | null;
  onChange: (value: string) => void;
  type: InputType;
  value?: string;
  label: string;
  name: string;
  multiline?: boolean;
  placeholder?: string;
  errors?: ErrorStatus;
  required?: boolean;
  sx?: SxProps<Theme>;
  autoComplete?: 'on' | 'off';
  disabled?: boolean;
  helperText?: string;
  helperVariant?: HelperVariant;
  step?: number | 'string';
  min?: number;
  max?: number;
  inputMode?: InputMode;
};

/**
 * InputMultiType
 *
 * Universal input supporting different types:
 * text, number, password, email, checkbox etc.
 * Uses MUI Input + InputLabel + FormControl.
 * It handles value changes via the `onChange` callback and displays validation errors
 *
 * @param label - displayed above the text input field.
 * @param placeholder - displayed inside the input when empty.
 * @param onChange - on every value change.
 * @param required - if the input is required.
 * @param errors - Error status object used to display validation messages after form submission.
 * @param defaultValue - The default value of the text input.
 * @param name - The name attribute of the input.
 * @param multiline - Enables multi-line input mode if true.
 * @param sx - Optional MUI `sx` style overrides for custom styling.
 * @param helperText - Text, that we show to user with information, when we don't have errors
 * @param helperVariant - Type and style for helperText, declared in clear-theme.ts
 * @param type - Type of the input element. It should be a valid HTML5 input type.
 * @param inputMode - global attribute that hints at the type of data that might be entered,
 *             this allows a browser to display an appropriate virtual keyboard.
 * @param autocomplete - permission the user agent has to provide automated assistance
 *             in filling out form field values;
 *           - takes values as in HTML documentation, not MUI
 */

function InputMultiType({
  label,
  type,
  placeholder,
  value,
  onChange,
  required = false,
  errors,
  defaultValue,
  name,
  multiline,
  sx,
  autoComplete,
  disabled = false,
  helperText,
  helperVariant = 'warning',
  step,
  min,
  max,
  inputMode,
}: InputMultiTypeProps) {
  const isError = errors?.tag === 'Submited' && errors.errors?.length > 0;
  const errorMessage = isError ? errors.errors.join(', ') : '';

  //Declain chenginging value in number input with scroll
  const numberInputRef = useDisableWheelOnNumberInput();

  // Local state for show/hide password
  const [showPassword, setShowPassword] = React.useState(false);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  //This is needed so that the button does not remove the focus from the input.
  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
  };

  // determine the actual input type
  const resolvedType =
    type === 'password' ? (showPassword ? 'text' : 'password') : type;

  return (
    <FormControl fullWidth error={isError} className="input-multi-type">
      <InputLabel
        shrink
        htmlFor={name}
        sx={{
          color: disabled ? 'rgba(0, 0, 0, 0.4)' : undefined,
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      >
        {/* we use required just for visibility, if it's not correct,
          we will catch this error in validator of component  */}
        {required ? `${label} *` : label}
      </InputLabel>

      <Input
        value={value}
        id={name}
        name={name}
        type={resolvedType}
        onChange={(e) => onChange(e.target.value)}
        defaultValue={defaultValue}
        sx={sx}
        multiline={multiline}
        autoComplete={autoComplete || 'off'}
        disabled={disabled}
        placeholder={placeholder ? placeholder : ''}
        inputMode={inputMode}
        inputRef={type === 'number' ? numberInputRef : undefined}
        inputProps={
          type === 'number'
            ? {
                step,
                min,
                max,
              }
            : undefined
        }
        // add the button with eye only for password type
        endAdornment={
          type === 'password' ? (
            <InputAdornment position="end">
              <IconButton
                className="toggle-password-btn"
                onClick={handleTogglePassword}
                onMouseDown={handleMouseDown}
                aria-label={showPassword ? 'hide password' : 'show password'}
                edge="end"
                disabled={disabled}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ) : undefined
        }
      />

      {isError ? (
        <FormHelperText>{errorMessage}</FormHelperText>
      ) : (
        helperText && (
          <FormHelperText
            sx={{
              ml: 1,
              mb: '10px',
              color: helperVariant === 'warning' ? 'warning.main' : 'info.main',
            }}
          >
            {helperText}
          </FormHelperText>
        )
      )}
    </FormControl>
  );
}

export default InputMultiType;
